import {format, getTime, formatDistanceToNow} from 'date-fns';
import {fr} from 'date-fns/locale';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// ----------------------------------------------------------------------

/**
 * Capitalise la première lettre d'une chaîne
 * @param {string} str - La chaîne à capitaliser
 * @return {string} La chaîne avec la première lettre en majuscule
 */
function capitalize(str) {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Formate une date selon un format spécifié ou par défaut en 'dd MMM yyyy'.
 * Capitalise automatiquement les noms de mois et jours complets (MMMM, EEEE).
 * @param {string|Date} date - La date à formater.
 * @param {string} [newFormat] - Le nouveau format souhaité pour la date.
 * @return {string} La date formatée.
 */
export function fDate(date, newFormat) {
  const fm = newFormat || 'dd/MM/yyyy';

  if (!date) return '';

  const formatted = format(new Date(date), fm, {locale: fr});

  // Capitaliser si le format contient des noms complets de mois ou jours
  if (fm.includes('MMMM') || fm.includes('EEEE')) {
    return capitalize(formatted);
  }

  return formatted;
}

export function fSimpleDate(date) {
  if (!date) return '';

  // On utilise directement les getUTC methods pour extraire jour/mois/année
  const d = new Date(date);
  const day = String(d.getUTCDate()).padStart(2, '0');
  const month = String(d.getUTCMonth() + 1).padStart(2, '0'); // +1 car les mois commencent à 0
  const year = d.getUTCFullYear();

  return `${day}/${month}/${year}`;
}

/**
 * Formate une date et heure selon un format spécifié ou par défaut en 'dd MMM yyyy à HH:mm'.
 * Utilisé pour afficher la date et l'heure en format 24 heures.
 * @param {string|Date} date - La date et l'heure à formater.
 * @param {string} [newFormat] - Le nouveau format souhaité pour la date et l'heure.
 * @return {string} La date et l'heure formatées.
 */
export function fDateTime(date, newFormat) {
  const fm = newFormat || 'dd/MM/yyyy à HH:mm';

  return date ? format(new Date(date), fm, {locale: fr}) : '';
}

dayjs.extend(utc);
dayjs.extend(timezone);
/**
 * Formate une date d'expiration de bon cadeau dans le fuseau de l'établissement.
 *
 * Rend le jour calendaire voulu quel que soit le fuseau du navigateur (cohérent avec
 * le PDF, l'e-mail et la page d'achat, qui passent tous par le fuseau de l'établissement).
 * Renvoie 'Indéterminée' pour un bon illimité (sentinelle année >= 9999).
 *
 * @param {string|Date} date - La date d'expiration à formater.
 * @param {string} [tz] - Fuseau IANA de l'établissement. À défaut, rendu en UTC.
 * @return {string} La date « DD/MM/YYYY », ou 'Indéterminée' si illimité, ou '' si absente.
 */
export function fDateValidity(date, tz) {
  if (!date) return '';

  // Bon illimité : sentinelle lointaine → libellé, quel que soit le fuseau.
  if (dayjs.utc(date).year() >= 9999) return 'Indéterminée';

  const formatted = tz ? dayjs(date).tz(tz) : dayjs.utc(date);
  return formatted.format('DD/MM/YYYY');
}

/**
 * Formate un objet Date en chaîne murale naïve « YYYY-MM-DDTHH:mm:00 » à partir
 * de ses composantes LOCALES (getFullYear, getHours…), sans fuseau ni décalage.
 *
 * Contrat d'heure : le front envoie l'intention murale, jamais un instant absolu
 * calculé par le navigateur (toISOString dépendrait du fuseau de la tablette).
 * Le backend ancre ensuite cette chaîne sur le fuseau de l'établissement
 * (cf. TimezoneService.parseClientDateTime). Utilisé à la saisie comme à
 * l'édition d'une réservation.
 *
 * @param {Date} localDate - Date dont les composantes locales portent l'heure voulue.
 * @return {string|null} Chaîne « YYYY-MM-DDTHH:mm:00 » (naïve, sans Z).
 */
export function formatNaiveDateTime(localDate) {
  if (!localDate) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${localDate.getFullYear()}-${pad(localDate.getMonth() + 1)}-${pad(localDate.getDate())}T${pad(localDate.getHours())}:${pad(localDate.getMinutes())}:00`;
}

/**
 * Récupère le timestamp d'une date.
 * @param {string|Date} date - La date pour obtenir le timestamp.
 * @return {number} Le timestamp de la date.
 */
export function fTimestamp(date) {
  return date ? getTime(new Date(date)) : '';
}

/**
 * Formate une date pour afficher le temps écoulé jusqu'à maintenant avec un suffixe.
 * Par exemple : 'il y a 3 jours'.
 * @param {string|Date} date - La date à partir de laquelle calculer le temps écoulé.
 * @return {string} Le temps écoulé depuis la date donnée jusqu'à maintenant.
 */
export function fToNow(date) {
  return date ?
    formatDistanceToNow(new Date(date), {
      addSuffix: true,
      locale: fr,
    }) :
    '';
}

/**
 * Formate une date en texte long localisé (ex : "Lundi 12 janvier 2025").
 *
 * Utilise l'API Intl native via `i18n.language` pour un affichage multilingue correct.
 * Première lettre capitalisée pour tous les locales.
 *
 * Si `timezone` est fourni (IANA, ex: 'Europe/Paris'), l'heure est calculée dans
 * ce fuseau horaire plutôt que dans celui du navigateur — critique pour les réservations
 * dont `dateTime` est stocké en UTC.
 *
 * @param {string} dateString - Date ISO à formater.
 * @param {string} language - Code langue i18next (ex: 'fr', 'en', 'de').
 * @param {string} [timezone] - Fuseau horaire IANA de l'établissement (optionnel).
 * @return {string} Date formatée ou '-' si absente.
 */
export function fReservationDate(dateString, language, timezone) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const options = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  if (timezone) options.timeZone = timezone;
  const formatted = date.toLocaleDateString(language, options);
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

/**
 * Formate une heure localisée (ex : "14:30").
 *
 * Si `timezone` est fourni (IANA, ex: 'Europe/Paris'), l'heure est calculée dans
 * ce fuseau horaire plutôt que dans celui du navigateur.
 *
 * @param {string} dateString - Date ISO dont on extrait l'heure.
 * @param {string} language - Code langue i18next.
 * @param {string} [timezone] - Fuseau horaire IANA de l'établissement (optionnel).
 * @return {string} Heure formatée ou '-' si absente.
 */
export function fReservationTime(dateString, language, timezone) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const options = {
    hour: '2-digit',
    minute: '2-digit',
  };
  if (timezone) options.timeZone = timezone;
  return date.toLocaleTimeString(language, options);
}
